import { useState, type MouseEvent } from 'react'
import { useSearchParams } from 'react-router-dom'
import Button from '@mui/material/Button'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import ListItemText from '@mui/material/ListItemText'
import ListItemIcon from '@mui/material/ListItemIcon'
import CheckIcon from '@mui/icons-material/Check'
import { useTranslation } from 'react-i18next'

import { useLocale, type LocaleCode } from '../../i18n/LocaleProvider'
import { FlagIcon } from '../common/FlagIcon'

/**
 * The language switcher.
 *
 * Each language is labelled in its own script, never translated. A Ukrainian visitor
 * scanning a list looks for "Українська", not for whatever the current interface calls
 * Ukrainian. The flag emoji is a wayfinding aid, not the label; several of these languages
 * are spoken in more countries than the flag suggests.
 */
export function LanguageSwitcher({ compact = false }: { compact?: boolean }) {
  const { t } = useTranslation()
  const { locale, setLocale, locales } = useLocale()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [searchParams, setSearchParams] = useSearchParams()

  const current = locales.find((l) => l.code === locale) ?? locales[0]

  const open = (event: MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget)
  const close = () => setAnchorEl(null)

  const choose = (code: LocaleCode) => {
    setLocale(code)
    close()
    // Keep ?lang= in the URL in step so the page stays shareable in the chosen language.
    // Routed rather than pushed through history.replaceState, so React Router sees it and
    // LocaleUrlSync agrees with the choice instead of reverting to the stale parameter.
    const params = new URLSearchParams(searchParams)
    params.set('lang', code)
    setSearchParams(params, { replace: true })
  }

  return (
    <>
      <Button
        onClick={open}
        color="inherit"
        aria-label={t('nav.language')}
        aria-haspopup="menu"
        aria-expanded={anchorEl ? 'true' : undefined}
        sx={{ minWidth: 0, px: compact ? 1 : 1.5, fontWeight: 500, gap: 0.75 }}
      >
        <FlagIcon locale={current.code} size={compact ? 22 : 20} />
        {!compact && <span>{current.nativeName}</span>}
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={close}
        slotProps={{ paper: { sx: { minWidth: 220, borderRadius: 3 } } }}
      >
        {locales.map((option) => (
          <MenuItem
            key={option.code}
            selected={option.code === locale}
            onClick={() => choose(option.code)}
            // The row keeps the menu's own direction rather than the language's. Setting
            // dir per item mirrors just the Hebrew row, throwing its flag to the opposite
            // side and breaking the column the eye scans down. The Hebrew label still
            // renders correctly: Unicode bidi handles the glyph run inside the label.
          >
            <ListItemIcon sx={{ minWidth: 34 }}>
              <FlagIcon locale={option.code} size={22} title={option.englishName} />
            </ListItemIcon>
            <ListItemText primary={option.nativeName} secondary={option.englishName} />
            {option.code === locale && <CheckIcon fontSize="small" color="primary" />}
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}
